import { ArrowRight, Check } from 'lucide-react-native';
import { ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Button } from '@/components/Button';
import { Heading } from '@/components/Heading';
import { Text } from '@/components/Text';
import { colors, radii, spacing } from '@/constants/theme';
import { addressLines } from '@/lib/voltrShipping';
import type { Action } from '@/types/deal';
import type { OrderedProduct, VoltrShipping } from '@/types/voltr';

import { WhileYouWaitCard } from '../WhileYouWaitCard';

import { OrderSummaryCard, SummaryLines, type OrderSummaryRow } from './OrderSummaryCard';

export type OrderPlacedProps = {
  product: Pick<OrderedProduct, 'title' | 'image_url'>;
  /** When Voltr recorded the order (ISO8601); the row hides until it's known. */
  placedAt?: string | null;
  /** Only right after ordering: the address is never stored, so a later visit has none. */
  shipTo?: VoltrShipping | null;
  /** Opens the brand's playbook; omit when it has none. */
  onReadPlaybook?: () => void;
  playbookError?: string | null;
  onDone: () => void;
};

/**
 * "Order Placed!" (Figma "Order 4"). No order number and no delivery
 * estimate: Voltr sends neither, and we don't invent them.
 */
export function OrderPlaced({ product, placedAt, shipTo, onReadPlaybook, playbookError, onDone }: OrderPlacedProps) {
  const rows: OrderSummaryRow[] = [];
  if (placedAt) rows.push({ label: 'Placed', value: <SummaryLines lines={[formatPlaced(placedAt)]} />, testID: 'voltr-placed-at' });
  if (shipTo) rows.push({ label: 'Ship to', value: <SummaryLines lines={addressLines(shipTo)} />, testID: 'voltr-placed-ship-to' });

  return (
    <View style={styles.screen} testID="voltr-order-placed">
      <ScrollView style={styles.flex} contentContainerStyle={styles.scroll}>
        <SafeAreaView edges={['top']} style={styles.header}>
          <View style={styles.check}>
            <Check size={36} color={colors.frost} strokeWidth={3} />
          </View>
          <Heading size="3xl" tone="inverse">
            Order placed!
          </Heading>
          <Text color="frost" align="center">
            We’ll let you know when it ships.
          </Text>
        </SafeAreaView>

        <View style={styles.body}>
          <OrderSummaryCard product={product} rows={rows} testID="voltr-placed-summary" />
          {onReadPlaybook ? <WhileYouWaitCard onPress={onReadPlaybook} testID="voltr-placed-playbook" /> : null}
          {playbookError ? (
            <Text variant="caption" color="danger" testID="voltr-placed-playbook-error">
              {playbookError}
            </Text>
          ) : null}
        </View>
      </ScrollView>
      <SafeAreaView edges={['bottom']} style={styles.footer}>
        <Button label="Back to deal" variant="primary" fullWidth trailingIcon={ArrowRight} onPress={onDone} testID="voltr-placed-done" />
      </SafeAreaView>
    </View>
  );
}

/** When Voltr recorded the order. */
export function orderPlacedAt(action: Action | null | undefined): string | undefined {
  return action?.voltr?.sample?.requested_at ?? undefined;
}

/** "10/7/2026 - 12:42 PM", in the phone's own time zone. */
export function formatPlaced(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.toLocaleDateString('en-US')} - ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

const CHECK = 72;

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.background },
  flex: { flex: 1 },
  scroll: { flexGrow: 1 },
  header: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.xl,
    backgroundColor: colors.deepGreen,
    borderBottomLeftRadius: radii.xl,
    borderBottomRightRadius: radii.xl,
  },
  check: {
    width: CHECK,
    height: CHECK,
    marginTop: spacing.xl,
    marginBottom: spacing.sm,
    borderRadius: radii.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.electricGreen,
  },
  body: { padding: spacing.lg, gap: spacing.base },
  footer: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
    paddingBottom: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
  },
});
